import { getContentConfig } from '../api/requests/player.requests.js';
import { PersistentCache } from '../offline/persistent_cache.js';
import { isConfigMissing } from '../utils/network_errors.js';

const NOTIFICATION_CONFIG_CACHE_KEY = 'notifications_config_v1';

export type NotificationImageConfig = string | string[];

export interface NotificationItemConfig
{
  enabled: boolean;
  id: number;
  key?: string;
  title: string;
  body: string;
  delay_hours: number;
  large_icon: string;
  big_picture: NotificationImageConfig;
}

export interface SmartNotificationSlotConfig
{
  enabled: boolean;
  hour: number;
  minute: number;
}

export interface SmartNotificationWeekConfig
{
  morning: SmartNotificationSlotConfig;
  afternoon: SmartNotificationSlotConfig;
  evening: SmartNotificationSlotConfig;
}

export interface SmartNotificationSessionPushConfig
{
  enabled: boolean;
  delay_minutes: number;
  large_icon: string;
  big_picture: NotificationImageConfig;
}

export interface SmartNotificationsConfig
{
  enabled: boolean;
  schedule_days: number;
  overlap_threshold_minutes: number;
  inactive_days_threshold: number;
  session_push: SmartNotificationSessionPushConfig;
  week1: SmartNotificationWeekConfig;
  week2_active: SmartNotificationWeekConfig;
  large_icon: string;
  big_picture: NotificationImageConfig;
}

export interface NotificationConfig
{
  notifications: NotificationItemConfig[];
  smart_notifications: SmartNotificationsConfig;
}

export const RANDOM_BANNER_MARKER = 'random_banner';
export const DEFAULT_LARGE_ICON = 'res://drawable/game_logo';
export const DEFAULT_BANNERS: string[] = [
  'res://drawable/notification_bg_1'
];

const DEFAULT_SMART_NOTIFICATIONS_CONFIG: SmartNotificationsConfig = {
  enabled: true,
  schedule_days: 11,
  overlap_threshold_minutes: 60,
  inactive_days_threshold: 3,
  session_push: {
    enabled: true,
    delay_minutes: 5,
    large_icon: DEFAULT_LARGE_ICON,
    big_picture: RANDOM_BANNER_MARKER
  },
  week1: {
    morning:   { enabled: true, hour: 10, minute: 0  },
    afternoon: { enabled: true, hour: 14, minute: 0  },
    evening:   { enabled: true, hour: 20, minute: 30 }
  },
  week2_active: {
    morning:   { enabled: false, hour: 10, minute: 0  },
    afternoon: { enabled: true,  hour: 14, minute: 0  },
    evening:   { enabled: true,  hour: 20, minute: 30 }
  },
  large_icon:  DEFAULT_LARGE_ICON,
  big_picture: RANDOM_BANNER_MARKER
};

const DEFAULT_NOTIFICATIONS: NotificationItemConfig[] = [];

const DEFAULT_NOTIFICATION_CONFIG: NotificationConfig = {
  notifications: DEFAULT_NOTIFICATIONS,
  smart_notifications: DEFAULT_SMART_NOTIFICATIONS_CONFIG
};

function asRecord( value: unknown ): Record<string, unknown>
{
  return value != null && typeof value === 'object' && !Array.isArray( value )
    ? value as Record<string, unknown>
    : {};
}

function clampInt( value: unknown, min: number, max: number, fallback: number ): number
{
  const n = typeof value === 'number' ? value : Number( value );
  if ( !Number.isFinite( n ) ) return fallback;
  return Math.max( min, Math.min( max, Math.floor( n ) ) );
}

function clampFloat( value: unknown, min: number, max: number, fallback: number ): number
{
  const n = typeof value === 'number' ? value : Number( value );
  if ( !Number.isFinite( n ) ) return fallback;
  return Math.max( min, Math.min( max, n ) );
}

function asBool( value: unknown, fallback: boolean ): boolean
{
  if ( typeof value === 'boolean' ) return value;
  if ( value === 1 || value === '1' || value === 'true' ) return true;
  if ( value === 0 || value === '0' || value === 'false' ) return false;
  return fallback;
}

function asString( value: unknown, fallback: string ): string
{
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
}

function asImageConfig( value: unknown, fallback: NotificationImageConfig ): NotificationImageConfig
{
  if ( typeof value === 'string' && value.trim().length > 0 ) return value.trim();

  if ( Array.isArray( value ) )
  {
    const items = value
      .map( item => typeof item === 'string' ? item.trim() : '' )
      .filter( item => item.length > 0 );
    if ( items.length > 0 ) return items;
  }

  return fallback;
}

function parseNotificationItem( src: unknown ): NotificationItemConfig | null
{
  const record = asRecord( src );
  if ( Object.keys( record ).length === 0 ) return null;

  const item: NotificationItemConfig = {
    enabled:     asBool( record.enabled, false ),
    id:          clampInt( record.id, 100, 999, 100 ),
    title:       asString( record.title, '' ),
    body:        asString( record.body, '' ),
    delay_hours: clampFloat( record.delay_hours, 0.01, 168, 24 ),
    large_icon:  asString( record.large_icon, '' ),
    big_picture: asImageConfig( record.big_picture, '' )
  };

  const key = asString( record.key, '' );
  if ( key ) item.key = key;

  return item;
}

function parseSlot( src: unknown, def: SmartNotificationSlotConfig ): SmartNotificationSlotConfig
{
  const record = asRecord( src );
  return {
    enabled: asBool( record.enabled, def.enabled ),
    hour:    clampInt( record.hour, 0, 23, def.hour ),
    minute:  clampInt( record.minute, 0, 59, def.minute )
  };
}

function parseWeek( src: unknown, def: SmartNotificationWeekConfig ): SmartNotificationWeekConfig
{
  const record = asRecord( src );
  return {
    morning:   parseSlot( record.morning,   def.morning ),
    afternoon: parseSlot( record.afternoon, def.afternoon ),
    evening:   parseSlot( record.evening,   def.evening )
  };
}

function parseSmartNotifications( src: unknown ): SmartNotificationsConfig
{
  const def = DEFAULT_SMART_NOTIFICATIONS_CONFIG;
  const record = asRecord( src );
  const session_push = asRecord( record.session_push );

  return {
    enabled:                   asBool( record.enabled, def.enabled ),
    schedule_days:             clampInt( record.schedule_days, 1, 60, def.schedule_days ),
    overlap_threshold_minutes: clampInt( record.overlap_threshold_minutes, 0, 1440, def.overlap_threshold_minutes ),
    inactive_days_threshold:   clampInt( record.inactive_days_threshold, 1, 30, def.inactive_days_threshold ),
    session_push: {
      enabled:       asBool( session_push.enabled, def.session_push.enabled ),
      delay_minutes: clampInt( session_push.delay_minutes, 1, 1440, def.session_push.delay_minutes ),
      large_icon:    asString( session_push.large_icon, def.session_push.large_icon ),
      big_picture:   asImageConfig( session_push.big_picture, def.session_push.big_picture )
    },
    week1:        parseWeek( record.week1, def.week1 ),
    week2_active: parseWeek( record.week2_active, def.week2_active ),
    large_icon:   asString( record.large_icon, def.large_icon ),
    big_picture:  asImageConfig( record.big_picture, def.big_picture )
  };
}

function normalizeNotificationConfig( raw: unknown ): NotificationConfig
{
  const root = asRecord( raw );
  const notifications_src = root.notifications;

  return {
    notifications: Array.isArray( notifications_src )
      ? notifications_src.map( parseNotificationItem ).filter( ( item ): item is NotificationItemConfig => item !== null )
      : DEFAULT_NOTIFICATIONS,
    smart_notifications: parseSmartNotifications( root.smart_notifications )
  };
}

export function collectHttpImageUrls( config: NotificationConfig ): string[]
{
  const urls = new Set<string>();
  const add = ( image: NotificationImageConfig | undefined ) =>
  {
    const values = Array.isArray( image ) ? image : image ? [ image ] : [];
    for ( const value of values )
    {
      if ( /^https?:\/\//i.test( value ) ) urls.add( value );
    }
  };

  for ( const notification of config.notifications ) add( notification.big_picture );
  add( config.smart_notifications.big_picture );
  add( config.smart_notifications.session_push.big_picture );

  return [ ...urls ];
}

export class NotificationConfigService
{
  static hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( NOTIFICATION_CONFIG_CACHE_KEY ) !== null;
  }

  private static cached_raw = PersistentCache.read<unknown>( NOTIFICATION_CONFIG_CACHE_KEY );
  private static config: NotificationConfig = NotificationConfigService.cached_raw != null
    ? normalizeNotificationConfig( NotificationConfigService.cached_raw )
    : DEFAULT_NOTIFICATION_CONFIG;
  private static loaded = NotificationConfigService.cached_raw != null;
  private static loading: Promise<NotificationConfig> | null = null;
  private static on_updated: ( ( config: NotificationConfig ) => void ) | null = null;

  static get(): NotificationConfig
  {
    return this.config;
  }

  static setOnUpdated( callback: ( ( config: NotificationConfig ) => void ) | null ): void
  {
    this.on_updated = callback;
  }

  static async refresh(): Promise<NotificationConfig>
  {
    if ( this.loading ) return this.loading;

    this.loading = ( async () =>
    {
      try
      {
        const server_config = await getContentConfig<unknown>( 'notifications' );
        this.config = normalizeNotificationConfig( server_config );
        this.loaded = true;
        PersistentCache.write( NOTIFICATION_CONFIG_CACHE_KEY, server_config );
        this.on_updated?.( this.config );
      }
      catch ( e )
      {
        if ( !this.loaded || isConfigMissing( e ) )
        {
          this.config = DEFAULT_NOTIFICATION_CONFIG;
          this.loaded = false;
          PersistentCache.remove( NOTIFICATION_CONFIG_CACHE_KEY );
        }
        console.warn( '[NotificationConfig] using defaults', e );
      }
      finally
      {
        this.loading = null;
      }

      return this.config;
    } )();

    return this.loading;
  }
}
